import { useState } from "react";
import CarrierBadge from "./CarrierBadge";
import { formatMXN, formatDate } from "@/lib/format";
import { carrierDisplayOverride } from "@/lib/carriers";
import { isFeaturedRate, FEATURED_CARD_CLS } from "@/lib/featuredRate";
import { ChevronDown, Gauge, FileText, Star, Ban, Check } from "lucide-react";
import PickupBadge from "./quote/PickupBadge";
import SavingsBadge from "./quote/SavingsBadge";
import OriginalCurrencyTooltip from "./quote/OriginalCurrencyTooltip";
import { getAggregatorLabel } from "@/lib/aggregatorLabel";

export default function RateCard({ rate, onSelect, selected, selectLabel, funded = true }) {
  const [showBreakdown, setShowBreakdown] = useState(false);
  const cheapest = rate._rank === "cheapest";
  const fastest = rate._rank === "fastest";
  const featured = isFeaturedRate(rate);

  return (
    <div className={`surface p-4 flex flex-col gap-3 transition-all min-w-0 ${featured ? FEATURED_CARD_CLS : ""} ${selected ? "border-cyan bg-cyan/5 glow-cyan ring-1 ring-cyan" : ""}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col items-start gap-1.5">
          <CarrierBadge carrier={rate.carrier} displayName={carrierDisplayOverride(rate.carrier, rate.source)} />
          <SourceBadge source={rate.source} />
          <PickupBadge pickup={rate.pickup} />
        </div>
        <div className="flex gap-1.5 flex-wrap justify-end">
          {featured && (
            <span className="flex items-center gap-1 px-2 py-0.5 text-[0.6rem] uppercase font-bold bg-violet-500/15 text-violet-400 dark:text-violet-300 border border-violet-500/50">
              <Star className="w-3 h-3 fill-current" /> Servicio Destacado
            </span>
          )}
          {rate.user_tariff_label && <span className="px-2 py-0.5 text-[0.6rem] uppercase font-bold bg-cyan/15 text-cyan border border-cyan/40">{rate.user_tariff_label}</span>}
          {cheapest && <span className="px-2 py-0.5 text-[0.6rem] uppercase font-bold bg-success/15 text-success border border-success/40">Más económica</span>}
          {fastest && <span className="px-2 py-0.5 text-[0.6rem] uppercase font-bold bg-cyan/15 text-cyan border border-cyan/40">Más rápida</span>}
        </div>
      </div>

      <div className="flex items-end justify-between">
        <div>
          <div className="label">Total MXN</div>
          <OriginalCurrencyTooltip rate={rate}>
            <div className={`font-display font-bold text-2xl leading-none ${featured ? "text-violet-500 dark:text-violet-300" : "text-cyan"} cursor-help`}>{formatMXN(rate.total_cost)}</div>
          </OriginalCurrencyTooltip>
          <SavingsBadge rate={rate} />
        </div>
        <div className="text-right">
          <div className="label">Entrega</div>
          <div className="font-display font-bold text-sm">{formatDate(rate.eta)}</div>
          <div className="text-[0.65rem] text-ink-muted flex items-center gap-1 justify-end">
            <Gauge className="w-3 h-3" /> {rate.transit_days} días
          </div>
        </div>
      </div>

      <div className="flex items-center gap-2 text-xs flex-wrap">
        {rate.zone && <span className="px-2 py-0.5 surface-inset font-mono">{rate.zone}</span>}
        {rate.service && <span className="text-ink-muted truncate">{rate.service}</span>}
        {rate.chargeableKg != null && (<><span className="text-ink-muted">·</span><span className="text-ink-muted">{rate.chargeableKg} kg</span></>)}
      </div>

      <button onClick={() => setShowBreakdown(v => !v)} className="flex items-center gap-1 text-xs text-ink-muted hover:text-ink-text">
        <ChevronDown className={`w-3 h-3 transition-transform ${showBreakdown ? "rotate-180" : ""}`} />
        Desglose
      </button>
      {showBreakdown && (
        <div className="surface-inset p-3 text-xs space-y-1.5 font-mono">
          <Row label="Tarifa base" value={formatMXN(rate.base_cost || rate.provider_cost || 0)} />
          {rate.fuel_surcharge > 0 && <Row label="Recargo combustible" value={`+ ${formatMXN(rate.fuel_surcharge)}`} />}
          {rate.special_handling > 0 && <Row label="Manejo especial" value={`+ ${formatMXN(rate.special_handling)}`} />}
          {rate.extended_zone_charge > 0 && <Row label={rate.epa_extended && rate.eda_extended ? "Zona ext. (origen+dest)" : rate.epa_extended ? "Zona ext. (origen · EPA)" : rate.eda_extended ? "Zona ext. (destino · EDA)" : "Zona extendida"} value={`+ ${formatMXN(rate.extended_zone_charge)}`} warn />}
          {rate.plus_zone_charge > 0 && <Row label={rate.epa_plus && rate.eda_plus ? "Zona plus (origen+dest)" : rate.epa_plus ? "Zona plus (origen · EPA)" : rate.eda_plus ? "Zona plus (destino · EDA)" : "Zona plus"} value={`+ ${formatMXN(rate.plus_zone_charge)}`} warn />}

          {rate.user_discount > 0 && (
            <div className="flex justify-between text-success">
              <span>{rate.user_tariff_label || "Descuento especial"}</span>
              <span>- {formatMXN(rate.user_discount)}</span>
            </div>
          )}
          {(Number(rate.insurance_fee || 0) + Number(rate.cudo_insurance_cost || 0) + Number(rate.cudo_margin || 0)) > 0 && <Row label="Cargos adicionales" value={`+ ${formatMXN(Number(rate.insurance_fee || 0) + Number(rate.cudo_insurance_cost || 0) + Number(rate.cudo_margin || 0))}`} />}
          {(Number(rate.platform_margin || 0) + Number(rate.other_charges || 0)) > 0 && <Row label="Otros cargos" value={`+ ${formatMXN(Number(rate.platform_margin || 0) + Number(rate.other_charges || 0))}`} accent />}
          <div className="border-t border-ink-border pt-1.5 flex justify-between font-bold text-cyan">
            <span>TOTAL MXN</span><span>{formatMXN(rate.total_cost)}</span>
          </div>
        </div>
      )}

      {funded ? (
        <button onClick={() => onSelect(rate)}
          className={`w-full flex items-center justify-center gap-2 py-2.5 font-display font-bold uppercase tracking-wide text-xs ${selected ? "bg-cyan text-ink ring-1 ring-cyan" : featured ? "bg-violet-500 hover:bg-violet-600 text-white" : "bg-cyan hover:bg-cyan-hover text-ink"}`}>
          {selected ? <><Check className="w-3.5 h-3.5" /> Seleccionada</> : <><FileText className="w-3.5 h-3.5" /> {selectLabel || "Elegir y Capturar Guía"}</>}
        </button>
      ) : (
        <button disabled
          className="w-full flex items-center justify-center gap-2 py-2.5 font-display font-bold uppercase tracking-wide text-xs border border-destructive/40 text-destructive bg-destructive/5 cursor-not-allowed"
          title="El convenio no está disponible">
          <Ban className="w-3.5 h-3.5" /> No disponible
        </button>
      )}
    </div>
  );
}

function SourceBadge({ source }) {
  const map = {
    interno: { label: "BHGC Nexus", cls: "border-cyan/40 text-cyan bg-cyan/10", dot: "bg-cyan" },
    envia: { label: "Tendencys", cls: "border-success/40 text-success bg-success/10", dot: "bg-success" },
    pak2go: { label: "Epak", cls: "border-warning/40 text-warning bg-warning/10", dot: "bg-warning" },
    interpack: { label: "Interpack", cls: "border-primary/40 text-primary bg-primary/10", dot: "bg-primary" },
    skypractico: { label: "SkyPractico", cls: "border-cyan/40 text-cyan bg-cyan/10", dot: "bg-cyan" },
    srsimi: { label: "SrSimi", cls: "border-rose-400/40 text-rose-400 bg-rose-400/10", dot: "bg-rose-400" },
    elogicya: { label: "ELogicYa", cls: "border-sky-400/40 text-sky-400 bg-sky-400/10", dot: "bg-sky-400" },
    sigomx: { label: "SigoMx", cls: "border-violet-400/40 text-violet-400 bg-violet-400/10", dot: "bg-violet-400" },
    enviot2: { label: "EnvioT2", cls: "border-teal-400/40 text-teal-400 bg-teal-400/10", dot: "bg-teal-400" },
    mydhl: { label: "SGM", cls: "border-amber-500/40 text-amber-600 bg-amber-500/10", dot: "bg-amber-500" },
    shipwoo: { label: "ShipWoo", cls: "border-lime-500/40 text-lime-600 bg-lime-500/10", dot: "bg-lime-500" },
    genienvios: { label: "GeniEnvios", cls: "border-emerald-400/40 text-emerald-600 bg-emerald-400/10", dot: "bg-emerald-400" },
    multipaq: { label: "MultiPaq", cls: "border-orange-400/40 text-orange-600 bg-orange-400/10", dot: "bg-orange-400" },
    slenvios: { label: "SLEnvios", cls: "border-pink-400/40 text-pink-600 bg-pink-400/10", dot: "bg-pink-400" },
    gmx: { label: "Gmx", cls: "border-slate-400/40 text-slate-600 bg-slate-400/10", dot: "bg-slate-400" },
    simulated: { label: "Simulada", cls: "border-ink-border text-ink-muted bg-ink-inset", dot: "bg-ink-muted" },
    bhgc: { label: "BHGC Nexus", cls: "border-cyan/40 text-cyan bg-cyan/10", dot: "bg-cyan" }
  };
  const s = map[source] || { label: getAggregatorLabel({ source }) || source || "Convenio", cls: "border-ink-border text-ink-muted bg-ink-inset", dot: "bg-ink-muted" };
  return (
    <span className={`flex items-center gap-1.5 px-2 py-0.5 text-[0.6rem] uppercase font-bold border ${s.cls}`}>
      <span className={`w-1.5 h-1.5 ${s.dot}`} /> {s.label}
    </span>
  );
}

function Row({ label, value, warn, accent }) {
  return (
    <div className="flex justify-between">
      <span className={accent ? "text-cyan" : "text-ink-muted"}>{label}</span>
      <span className={accent ? "text-cyan" : warn ? "text-warning" : "text-ink-text"}>{value}</span>
    </div>
  );
}